import { HOUSEHOLD_CAPACITY } from "game/household_capacity";
import { lifeClockRate, lifeClockSeasons } from "game/social_clock";
import { initialiseOrientation, mutuallyOriented } from "game/orientation";
import { isAdult, isDependent, canSeekPartner, canParentTogether, personSize, LIFE_YEARS_PER_SEASON, LIFE_YEARS_PER_GAME_YEAR, oldAgeDeathChance } from "game/life_stages";
import { PERSON_COMPONENTS, updateDirectoryPerson, withDetachedPeople } from "game/person_storage";
import { rememberNewsScopes } from "game/event_knowledge";
import { residentPeople, withPopulationIndex, refreshPersonIndex } from "game/population_index";
import { historySecretActivities } from "game/secret_activity_history";
import { runSocialActions, runSeasonSocialActions } from "game/social_actions";
import { relatedPeople, withCloseFamilyIndex } from "game/social_kin";
import { finishTransition } from "game/history_scope";
import { exchangeNews, listenerNewsScore, beginSocialSpeech } from "game/news";
import { DAYS_IN_SEASON } from "game/time";
import { advanceOffscreenNeeds } from "game/needs";
import { prepareSettlementSeason, settleSeason, constructHome, HOUSE_TIMBER } from "game/settlement";
import { consumeTimber } from "game/settlement_growth";
import { amountOf, TIMBER } from "game/goods";
import { recordJourney, routeTile } from "game/settlement_routes";
import { entityId, isResident } from "game/residents";
import { Hex } from "game/hex";
// No map, renderer, DOM or pathfinding imports: also runs on an unvisited world hex.
import MersenneTwister from "mersenne-twister";
import { addGoodwill, disposition, goodwillTowards, generatePersonality, meet, TRAIT_PAIRS, copyRelationshipList, drift, peopleInRings, withRelationshipIndex } from "game/people";
import { hashUnit } from "game/math";
import { recordEvent, eventById, closeFamily, withEventIndex, invalidateEventIndex } from "game/events";
import { die, repairHouseholds } from "game/possessions";
export const SOCIAL_RATES = {
  birthPerSeason: .85,
  maxBirthChance: .9,
  birthCooldownSeasons: 1,
  travellerShare: 0.05,
  courtshipSeasons: lifeClockSeasons(2),
  practicalCourtshipSeasons: 1,
  recentContactSeasons: lifeClockSeasons(1)
};
const alive = (s, id) => s.ecs.personable[id] && !s.ecs.personable[id].dead;
const roll = (s, key) => hashUnit(s.peopleSeed + ":social:" + key);
const living = s => residentPeople(s).filter(id => alive(s, id) && isResident(s, id) && !s.ecs.playable?.[id]);
const distance = (s, a, b) => {
  const p = s.ecs.spatial?.[a],
    q = s.ecs.spatial?.[b];
  return p && q ? (p.x - q.x) ** 2 + (p.y - q.y) ** 2 : Infinity;
};
const add = (s, components) => {
  const id = entityId(s.ecs, "social-");
  if (components.personable) initialiseOrientation(components, id);
  for (const [key, value] of Object.entries(components)) (s.ecs[key] ||= {})[id] = {
    ...value,
    id
  };
  if (components.personable && s.residents) s.residents[id] = true;
  if (s.communityHexes && components.building) s.communityHexes[id] = s.communityHexes[s.villages[components.village.villageId].houses[0]];
  const directory=s.newsContext;
  if(components.personable && directory?.localPeople) {
    directory.people[id]=Object.fromEntries(PERSON_COMPONENTS.filter(key=>s.ecs[key]?.[id]).map(key=>[key,s.ecs[key][id]]));
    updateDirectoryPerson(directory,id);
  }
  if (components.spatial?.immovable && s.space) {
    const key = Hex().fromPoint(components.spatial).toString();
    s.space[key] = [...(s.space[key] || []), id];
  }
  refreshPersonIndex(s,id);
  s.redraws?.push(id);
  return id;
};

// Caches exist only inside an owned history transition. Imports and
// caller edits never reuse them. Prehistory owns its graph until generation ends.
const socialIndexes = new WeakMap();
export function withSocialIndex(s, fn) {
  if (socialIndexes.has(s)) return fn();
  socialIndexes.set(s, {
    ancestry: new Map(),
    affinity: new Map()
  });
  return finishTransition(fn, () => socialIndexes.delete(s));
}
// Recompute the shallow graph: imports/births can add a previously missing ancestor.
export function related(s, a, b, includeLineal = false) {
  return relatedPeople(s,a,b,includeLineal,socialIndexes.get(s)?.ancestry);
}

function mutualAttraction(s, a, b) {
  const index=socialIndexes.get(s)?.affinity;
  if(index && !index.has(a))index.set(a,new Map());
  const cache=index?.get(a);
  if(cache?.has(b))return cache.get(b);
  const answer=mutuallyOriented(s,a,b) && disposition(s,a,b)=="love" && disposition(s,b,a)=="love" && !related(s,a,b);
  cache?.set(b,answer);return answer;
}

// Eligibility is local-map wide. This does not imply attraction or consent.
export function eligiblePartner(s, a, b, deliberate = false) {
  return !!s.ecs.kinship?.[a] && !!s.ecs.kinship?.[b] && a != b && isResident(s, a) && isResident(s, b) && alive(s, a) && alive(s, b) && (deliberate || !s.ecs.playable?.[a] && !s.ecs.playable?.[b]) && isAdult(s.ecs.personable[a].age) && isAdult(s.ecs.personable[b].age) && (deliberate || canSeekPartner(s.ecs.personable[a].age) && canSeekPartner(s.ecs.personable[b].age)) && (!s.ecs.kinship[a]?.spouse || !alive(s, s.ecs.kinship[a].spouse)) && (!s.ecs.kinship[b]?.spouse || !alive(s, s.ecs.kinship[b].spouse)) && [a,b].every((id,i)=>!s.ecs.relationships?.[id]?.commitment || s.ecs.relationships[id].commitment.partnerId==[b,a][i]) && !related(s, a, b, deliberate);
}
export const eligiblePartners = (s, a) => living(s).filter(b => eligiblePartner(s, a, b));
export const romanticInterest = (s, a, b) => eligiblePartner(s, a, b) && mutualAttraction(s, a, b);
const willing = (s,a,b) => disposition(s,a,b)!="hate" && disposition(s,b,a)!="hate" && goodwillTowards(s,a,b)>=0 && goodwillTowards(s,b,a)>=0;
export const attractionRank = (s,a,b) => mutuallyOriented(s,a,b) && willing(s,a,b) ? mutualAttraction(s,a,b)?2:1 : 0;
export const partnerInterest = (s,a,b) => eligiblePartner(s,a,b) ? attractionRank(s,a,b) : 0;
export function courtshipPreference(s, a, b, proximity = 1) {
  const ageGap = Math.abs(s.ecs.personable[a].age - s.ecs.personable[b].age);
  return proximity * (1 + Math.max(0, s.ecs.relationships[a].known[b]?.goodwill || 0) / 12) / (1 + ageGap / 5) ** 2;
}
export function compareSocial(s, a, b, c, newsScores=new Map()) {
  const score=id=>{
    if(!newsScores.has(id))newsScores.set(id,listenerNewsScore(s,a,id));
    return newsScores.get(id);
  };
  const romanticB = partnerInterest(s, a, b), romanticC = partnerInterest(s, a, c);
  const value = id => romanticB && romanticC ? courtshipPreference(s, a, id, s.ecs.village[a]?.villageId === s.ecs.village[id]?.villageId ? 1 : .35) : s.ecs.relationships[a].known[id]?.goodwill || 0;
  return Number(romanticC) - Number(romanticB) || value(c) - value(b) || score(c)-score(b);
}
export function canCourt(s, a, b, deliberate = false) {
  return eligiblePartner(s,a,b,deliberate) && mutuallyOriented(s,a,b) && willing(s,a,b) &&
    (!!betrothal(s,a,b) || ["personal","intimate"].includes(s.ecs.relationships?.[a]?.known[b]?.ring) && ["personal","intimate"].includes(s.ecs.relationships?.[b]?.known[a]?.ring));
}
const betrothal = (s,a,b) => s.ecs.relationships?.[a]?.commitment?.partnerId==b && s.ecs.relationships?.[b]?.commitment?.partnerId==a ? s.ecs.relationships[a].commitment : null;
const loveMatch = (s,a,b) => mutualAttraction(s,a,b) && [a,b].every((id,i)=>s.ecs.relationships[id].known[[b,a][i]]?.ring=="intimate");
const courtshipWait = (s,a,b) => betrothal(s,a,b)?.waitSeasons ?? (loveMatch(s,a,b) ? SOCIAL_RATES.courtshipSeasons : SOCIAL_RATES.practicalCourtshipSeasons);
export function clearCourtship(s,a,b) {
  delete s.social.courtships[[a,b].sort().join(":")];
  for(const [id,other] of [[a,b],[b,a]])if(s.ecs.relationships?.[id]?.commitment?.partnerId==other)delete s.ecs.relationships[id].commitment;
}

// Empty does not mean ownerless: an inherited estate may have an absentee owner.
export function availableHouse(s, houseId, people = []) {
  const owner = s.ecs.controllable?.[houseId]?.controllerId;
  return !owner || owner == houseId || !alive(s, owner) || people.includes(owner);
}
function familyHomeAvailable(s, houseId, people) {
  const owner = s.ecs.controllable?.[houseId]?.controllerId;
  return availableHouse(s, houseId, people) || people.some(id => closeFamily(s, owner).includes(id));
}
export function moveHouse(s, id, houseId, summary = false) {
  const fromHouse = s.ecs.household[id]?.houseId,
    fromVillage = s.ecs.village[id]?.villageId;
  if (fromHouse == houseId) return;
  rememberNewsScopes(s,id);
  if (availableHouse(s, houseId, [id])) s.ecs.controllable[houseId].controllerId = id;
  s.ecs.household[id] = {
    id,
    houseId,
    since: s.days
  };
  s.ecs.village[id] = {
    ...s.ecs.village[id],
    id,
    villageId: s.ecs.village[houseId]?.villageId ?? null
  };
  if (s.communityHexes) s.communityHexes[id] = s.communityHexes[houseId];
  refreshPersonIndex(s,id);
  invalidateEventIndex(s);
  const w = s.ecs.planner?.[id]?.world;
  if (w) {
    w.places = {};
    w.no_place_for = {};
    delete w.social;
    s.ecs.planner[id].task = null;
    s.ecs.planner[id].plan = null;
    for (const farm of Object.values(s.ecs.farmable || {})) {
      if (farm.claimerId == id) farm.claimerId = null;
      for (const slot of farm.slots) if (slot.workerId == id) delete slot.workerId;
    }
    delete w.helpingFriend;
    delete w.friendOffer;
    w.claimedFarmable = null;
    w.currentJob = null;
    w.subtasks = null;
    w.moveHome = houseId;
    for (const bed of Object.values(s.ecs.sleepable || {})) bed.occupiers = bed.occupiers.filter(p => p != id);
    for (const manager of Object.values(s.ecs.manager || {})) for (const job of manager.jobs) if (job.assignedId == id) job.assignedId = null;
  }
  if (summary && s.ecs.spatial[houseId]) s.ecs.spatial[id] = {
    ...s.ecs.spatial[id],
    x: s.ecs.spatial[houseId].x,
    y: s.ecs.spatial[houseId].y
  };
  refreshPersonIndex(s,id);
  recordEvent(s, "move", [id], [], {
    fromHouse,
    toHouse: houseId,
    fromVillage,
    toVillage: s.ecs.village[id].villageId
  });
}
export function canHaveChild(s,a,b) {
  return parentWindows(s,a,b) && s.ecs.household[a]?.houseId == s.ecs.household[b]?.houseId && !related(s,a,b);
}
const parentWindows = (s,a,b) => canParentTogether(...[a,b].map(id => ({ male: s.ecs.nameable[id]?.male, age: s.ecs.personable[id].age })));
function householdCounts(s,roster,excluded=new Set()) {
  const counts={},present=new Set();
  for(const id of roster)if(alive(s,id) && isResident(s,id) && !excluded.has(id)) {
    const house=s.ecs.household[id]?.houseId;
    counts[house]=(counts[house]||0)+1;present.add(id+":"+house);
  }
  // Travellers retain a place in their real home. Their small visit ledger
  // supplies absent members without searching the world people directory.
  for(const [id,visit] of Object.entries((s.newsContext||s.travel)?.visits||{})) {
    const house=visit.household?.houseId;
    if(excluded.has(id) || present.has(id+":"+house) || !alive(s,id) || !s.ecs.building?.[house] && s.ecs.shelter?.[house]?.stage!="house")continue;
    counts[house]=(counts[house]||0)+1;
  }
  return counts;
}
function coupleHouse(s, a, b, dependents = []) {
  const ecs = s.ecs,
    village = s.villages?.[ecs.village[a]?.villageId];
  const counts=householdCounts(s,residentPeople(s),new Set([a,b,...dependents]));
  const members = house => counts[house] || 0;
  const family = [...new Set([ecs.household[a]?.houseId, ecs.household[b]?.houseId])].filter(id => ecs.building?.[id] || ecs.shelter?.[id]?.stage == "house");
  const rooms = family.filter(id => members(id) <= HOUSEHOLD_CAPACITY - 2 - dependents.length && (members(id) > 0 || availableHouse(s, id, [a, b])));
  const room = (s.communityHexes ? rooms.sort((a,b) => members(a)-members(b)) : rooms)[0];
  if (room) return room;
  const villageHomes = [...new Set([...(village?.houses || []), ...(s.villages?.[ecs.village[b]?.villageId]?.houses || []), ...Object.keys(ecs.building || {}).filter(id => [a, b].includes(ecs.controllable[id]?.controllerId))])];
  const empty = villageHomes.find(id => members(id) == 0 && familyHomeAvailable(s, id, [a, b]));
  if (empty) return empty;
  if (!village) return null;
  if (s.settlement) return constructHome(s,village.id,[a,b,...dependents]);
  if (village.houses.length >= 8) return null;
  // A summary world can provide build sites as plain coordinates. Rendered worlds
  // derive them from reachable village grassland, outside existing plots.
  const origin = ecs.spatial[village.houses[0]];
  const buildings = Object.keys(ecs.building || {}).filter(id => !s.communityHexes || s.communityHexes[id] === s.communityHexes[village.houses[0]]);
  const sites = s.houseSites?.[village.id] || (s.compact && origin ? Array.from({ length: 8 }, (_, i) => ({ x: origin.x + (i % 4) * 210, y: origin.y + Math.floor(i / 4) * 210 })) : Object.keys(ecs.walkable || {}).filter(id => ecs.mappable[id]?.terrain == "grassland" && !ecs.mappable[id].villagePlot && distance(s, id, a) < 600 ** 2 && !buildings.some(h => distance(s, id, h) < 150 ** 2)).map(id => ecs.spatial[id]));
  const point = sites.find(p => !buildings.some(id => (ecs.spatial[id].x - p.x) ** 2 + (ecs.spatial[id].y - p.y) ** 2 < 150 ** 2));
  if (!point) return null;
  const fundingHouse = ecs.household[a]?.houseId, sourceStore = ecs.household[fundingHouse]?.storeId;
  if (s.compact && amountOf(ecs.container?.[sourceStore]?.amounts, TIMBER) < HOUSE_TIMBER) return null;
  if (s.compact) consumeTimber(s, fundingHouse, HOUSE_TIMBER, sourceStore);
  const tile = Object.keys(ecs.mappable || {}).find(id => Math.abs(ecs.spatial[id].x - point.x) < 0.1 && Math.abs(ecs.spatial[id].y - point.y) < 0.1);
  if (tile) ecs.mappable[tile] = {
    ...ecs.mappable[tile],
    villagePlot: true
  };
  const house = add(s, {
    spatial: {
      ...point,
      immovable: true
    },
    nameable: {
      nickname: "Family cottage"
    },
    building: {
      entrance: 0,
      rooms: []
    },
    household: {
      farming: false,
      summaryFields: s.compact ? 2 : undefined,
      fields: [],
      backyards: []
    },
    controllable: {
      controllerId: a
    },
    village: {
      villageId: village.id
    }
  });
  const bed = add(s, {
    spatial: {
      ...point
    },
    sleepable: {
      capacity: HOUSEHOLD_CAPACITY,
      level: 1,
      occupiers: []
    },
    interior: {
      buildingId: house
    },
    controllable: {
      controllerId: house
    }
  });
  const store = add(s, {
    spatial: {
      ...point
    },
    container: {
      capacity: 114,
      amounts: {}
    },
    interior: {
      buildingId: house
    },
    controllable: {
      controllerId: house
    }
  });
  ecs.building[house].rooms = [bed, store];
  ecs.household[house].storeId = store;
  village.houses.push(house);
  if (s.compact) recordEvent(s, "house_built", [a, b, ...dependents], [house], { houseId: house, villageId: village.id,
    timber: HOUSE_TIMBER, sourceStore, reason: "household establishment", summary: true });
  return house;
}
export function marry(s, a, b, summary = false, deliberate = false) {
  if (!canCourt(s, a, b, deliberate)) return false;
  const promise=betrothal(s,a,b);
  if(promise && s.days/DAYS_IN_SEASON-promise.since/DAYS_IN_SEASON<promise.waitSeasons)return false;
  const kind=promise?.kind || (loveMatch(s,a,b)?"love":"practical");
  const dependents = [...new Set([a, b].flatMap(id => s.ecs.kinship[id].children))].filter(id => alive(s, id) && isDependent(s.ecs.personable[id].age) && [s.ecs.household[a]?.houseId, s.ecs.household[b]?.houseId].includes(s.ecs.household[id]?.houseId));
  if (dependents.length > HOUSEHOLD_CAPACITY - 2) return false;
  const house = coupleHouse(s, a, b, dependents);
  if (!house) return false;
  // Retain previous spouses in event history; current spouse links are reciprocal.
  for (const id of [a, b]) {
    const old = s.ecs.kinship[id].spouse;
    if (s.ecs.kinship[old]) s.ecs.kinship[old].spouse = null;
  }
  s.ecs.kinship[a].spouse = b;
  s.ecs.kinship[b].spouse = a;
  for (const id of [a, b]) if (s.ecs.guest?.[id]) delete s.ecs.guest[id];
  const fromVillages = [s.ecs.village[a]?.villageId, s.ecs.village[b]?.villageId];
  for (const id of [a, b, ...dependents]) moveHouse(s, id, house, summary);
  recordEvent(s, "marriage", [a, b], [], {
    kind,
    ...(promise?{head:promise.head,otherHead:promise.otherHead,betrothalId:promise.eventId}:{}),
    fromVillages,
    houseId: house
  });
  if(s.social?.courtships)clearCourtship(s,a,b);
  repairHouseholds(s);
  return true;
}
export function birth(s, a, b, season) {
  if (!parentWindows(s,a,b)) return null;
  const ecs = s.ecs,
    generator = new MersenneTwister(Math.floor(roll(s, "child:" + a + ":" + season) * 4294967296));
  const personality = generatePersonality(generator);
  // Keep opposed traits out and distinct tastes; inherit one pair and one taste set.
  const trait = ecs.personality[a].traits[generator.random_int() % ecs.personality[a].traits.length];
  const pair = TRAIT_PAIRS.find(p => p.includes(trait));
  personality.traits = [trait, ...personality.traits.filter(t => !pair.includes(t))].slice(0, 3);
  personality.likes = [...ecs.personality[b].likes];
  personality.dislikes = [...ecs.personality[b].dislikes];
  const male = generator.random_int() % 2 == 0;
  const child = add(s, {
    spatial: {
      ...ecs.spatial[a],
      immovable: false
    },
    nameable: {
      type: "person",
      male,
      familySeed: ecs.nameable[a]?.familySeed,
      seed: generator.random_int()
    },
    personable: {
      age: 0,
      lifeYears: 0,
      born: season * DAYS_IN_SEASON,
      size: 12,
      male,
      body: male ? 0x4E3F30 : 0x3B6071,
      hair: ecs.personable[a].hair || 0,
      hunger: 0,
      tiredness: 0
    },
    kinship: {
      parents: [a, b],
      children: [],
      spouse: null
    },
    personality: {
      ...personality,
      ...(!s.generatingHistory && ecs.personality[a].opinionThresholds ? {
        opinionSeed: ecs.personality[a].opinionSeed,
        opinionThresholds: {
          ...ecs.personality[a].opinionThresholds
        }
      } : {})
    },
    household: {
      houseId: ecs.household[a]?.houseId
    },
    village: {
      villageId: ecs.village[a]?.villageId
    },
    controllable: {
      controllerId: ecs.controllable[a]?.controllerId || a
    },
    traits: {
      values: {}
    },
    attributes: {},
    holder: {
      held: [],
      capacity: 2
    },
    relationships: {
      known: {}
    },
    manager: {
      jobs: []
    },
    planner: {
      world: {
        no_place_for: {},
        jobs: [],
        places: {},
        feeling: {},
        holding: {},
        days: s.days
      }
    }
  });
  refreshPersonIndex(s,child);
  invalidateEventIndex(s);
  for (const p of [a, b]) {
    ecs.kinship[p].children.push(child);
    ecs.personable[p].lastBirth = season;
  }
  for (const other of new Set([...living(s), a, b])) if (other != child && ecs.village[other]?.villageId == ecs.village[child].villageId) {
    meet(s, child, other);
    meet(s, other, child);
    if ([a, b].includes(other)) {
      addGoodwill(s, child, other, 36);
      addGoodwill(s, other, child, 36);
    }
  }
  recordEvent(s, "birth", [child, a, b]);
  return child;
}
export function chatTogether(s, a, b, summary = false, conversations = 1) {
  if (!alive(s, a) || !alive(s, b) || !summary && distance(s, a, b) > 85 ** 2) return false;
  s.social ||= {
    courtships: {},
    avoidance: {}
  };
  s.social.avoidance ||= {};
  const key = a < b ? a + ":" + b : b + ":" + a,
    until = s.social.avoidance[key];
  if (until > s.days) return false;
  s.social.contacts ||= {};
  s.social.contacts[key] = s.days;
  meet(s,a,b);meet(s,b,a);
  const hostile = disposition(s, a, b) == "hate" || disposition(s, b, a) == "hate";
  if (summary && hostile) conversations = 1;
  const actions=runSocialActions(s,"contact",a,b,{summary,conversations});
  if(actions.some(a=>a.stop))return true;
  for (const [from, to] of [[a, b], [b, a]]) addGoodwill(s, from, to, lifeClockRate({
    hate: -0.25,
    like: 1,
    love: 4
  }[disposition(s, to, from)]) * conversations);
  const visits=[s.ecs.planner?.[a]?.world.social,s.ecs.planner?.[b]?.world.social];
  const exchanged=!summary && visits.some(v=>v?.newsExchanged);
  const spoken=exchanged?[]:exchangeNews(s,a,b);
  if(!summary) {
    for(const v of visits)if(v)v.newsExchanged=true;
    for(const [speaker,listener] of [[a,b],[b,a]]) {
      const line=spoken.find(l=>l.speakerId==speaker);
      beginSocialSpeech(s,speaker,listener,line&&eventById(s,line.eventId));
    }
  }
  return true;
}

// Grown children leave crowded homes when a local house is empty. This gives
// families access to the village's existing fields instead of crowding one plot.
function settleAdults(s, summary) {
  const ecs = s.ecs,
    roster = living(s).filter(id => !s.ecs.guest?.[id]),
    residents = {};
  for (const id of roster) (residents[ecs.household?.[id]?.houseId] ||= []).push(id);
  const members = house => residents[house] || [];
  for (const village of Object.values(s.villages || {})) {
    for (const house of village.houses || []) {
      if (members(house).length) continue;
      const candidates = roster.filter(id => familyHomeAvailable(s, house, [id, ecs.kinship[id]?.spouse]) && isAdult(ecs.personable[id].age) && ecs.kinship[id]?.parents.length && ecs.controllable[ecs.household[id]?.houseId]?.controllerId != id && members(ecs.household[id]?.houseId).length > 2).sort((a, b) => ecs.personable[a].age - ecs.personable[b].age || a.localeCompare(b));
      for (const id of candidates) {
        const from = ecs.household[id].houseId,
          spouse = ecs.kinship[id].spouse;
        const family = [id];
        if (alive(s, spouse) && ecs.household[spouse]?.houseId == from) family.push(spouse);
        family.push(...ecs.kinship[id].children.filter(child => alive(s, child) && isDependent(ecs.personable[child].age) && ecs.household[child]?.houseId == from));
        if (family.some(id=>ecs.playable?.[id]) || members(from).length - family.length < 2) continue;
        for (const person of family) moveHouse(s, person, house, summary);
        residents[from] = members(from).filter(id => !family.includes(id));
        residents[house] = family;
        break;
      }
    }
  }
}

// Retry mature courtships when fresh contact occurs between seasonal updates.
function updateCourtships(s, season = Math.floor(s.days / DAYS_IN_SEASON), summary = false, roster = living(s).filter(id=>s.ecs.relationships?.[id] && s.ecs.kinship?.[id] && s.ecs.personality?.[id])) {
  const used = new Set(),pairs=[];
  for (const a of roster) {
    const candidates=[...new Set([s.ecs.relationships[a].commitment?.partnerId,...peopleInRings(s,a,["personal","intimate"])].filter(Boolean))];
    for(const b of candidates)if(a<b && isResident(s,b))pairs.push([a,b]);
  }
  pairs.sort(([a,b],[c,d])=>Number(loveMatch(s,c,d))-Number(loveMatch(s,a,b)));
  for(const [a,b] of pairs)if(!used.has(a) && !used.has(b)) {
    if(courtTogether(s,a,b,season,summary)) {
      used.add(a);used.add(b);
    }
  }
}

export function courtTogether(s, a, b, season, summary = false) {
  const key = [a, b].sort().join(":");
  if (!canCourt(s, a, b)) { clearCourtship(s,a,b); return false; }
  if(!betrothal(s,a,b) && (s.social.contacts?.[key] ?? -Infinity) < (summary ? s.days : s.days-SOCIAL_RATES.recentContactSeasons*DAYS_IN_SEASON))return false;
  const started = s.social.courtships[key] ?? season;
  s.social.courtships[key] = started;
  return season - started >= courtshipWait(s,a,b) && marry(s, a, b, summary);
}

// Hourly contact can arrive just after a seasonal wedding check. Retry only
// existing courtships so their original seasonal start and delay stay intact.
export function retryCourtships(s) {
  const season = s.days / DAYS_IN_SEASON;
  const pending=Object.entries(s.social?.courtships || {});
  pending.sort(([left],[right])=>Number(loveMatch(s,...right.split(":")))-Number(loveMatch(s,...left.split(":"))));
  for (const [key, started] of pending) {
    const [a,b] = key.split(":");
    if(!isResident(s,a) || !isResident(s,b))continue;
    if (season - started < courtshipWait(s,a,b)) continue;
    courtTogether(s,a,b,season);
  }
}

// Live play and summary history share every demographic transition. Only
// summary encounters stand in for navigation and timed conversations.
export function advanceSeason(s, season, summary = false) {
  const previous=s.summarisingHistory;
  s.summarisingHistory=summary;
  try {return withPopulationIndex(s, () => withSocialIndex(s, () => withEventIndex(s, () => withRelationshipIndex(s, () => advanceSeasonRules(s, season, summary)))));}
  finally {if(previous===undefined)delete s.summarisingHistory;else s.summarisingHistory=previous;}
}
function advanceSeasonRules(s, season, summary) {
  if (s.social?.lastSeason >= season) return;
  prepareSettlementSeason(s,season);
  s.social ||= {};
  s.social.courtships ||= {};
  s.social.avoidance ||= {};
  const ecs = s.ecs,
    ids = residentPeople(s).filter(id => alive(s,id) && isResident(s,id) && (!ecs.playable?.[id] || ecs.playable[id].arrivedAt != null) && (!s.generatingHistory || !ecs.playable?.[id])).filter(id => ecs.kinship?.[id] && ecs.relationships?.[id] && ecs.personality?.[id]);
  for (const key of Object.keys(s.social.courtships)) {
    const [a, b] = key.split(":");
    // Absence is already priced by drift. Keep time spent with mutual consent;
    // a recent real conversation is still required before a live wedding.
    // A neighbouring courtship is checked at the shared world boundary, where
    // both actual communities are present. A local season must keep its time.
    if (isResident(s, a) && isResident(s, b) && !canCourt(s, a, b)) clearCourtship(s,a,b);
  }
  for (const id of ids) {
    const p = ecs.personable[id];
    p.age += LIFE_YEARS_PER_SEASON;
    p.lifeYears = p.age / LIFE_YEARS_PER_GAME_YEAR;
    p.size = personSize(p.age);
    s.redraws?.push(id);
    if (roll(s, "death:" + id + ":" + season) < oldAgeDeathChance(p.age)) die(s, id);
  }
  settleAdults(s, summary);
  const participants = ids.filter(id => alive(s, id));
  const roster = participants.filter(id => !ecs.playable?.[id]);
  withCloseFamilyIndex(s, () => {
    runSeasonSocialActions(s,participants,summary);
    if (summary) {
      const singles = new Set(roster.filter(id => canSeekPartner(ecs.personable[id].age) && (!ecs.kinship[id].spouse || !alive(s, ecs.kinship[id].spouse))));
      const byVillage = new Map(),
        live = new Set(roster);
      for (const id of roster) {
        const village = ecs.village[id]?.villageId;
        if (!byVillage.has(village)) byVillage.set(village, []);
        byVillage.get(village).push(id);
      }
      const avoiding = new Map();
      for (const key of Object.keys(s.social.avoidance)) if (s.social.avoidance[key] > s.days) {
        const [a, b] = key.split(":");
        if (!avoiding.has(a)) avoiding.set(a, new Set());
        if (!avoiding.has(b)) avoiding.set(b, new Set());
        avoiding.get(a).add(b);
        avoiding.get(b).add(a);
      }
      // Only paired people change avoidance during this pass, and both have
      // already left the candidate pool before their conversation starts.
      const paired = new Set();
      const order = roster.map(id => ({
        id,
        order: roll(s, season + ":order:" + id)
      })).sort((a, b) => Number(singles.has(b.id)) - Number(singles.has(a.id)) || a.order - b.order).map(p => p.id);
      for (const a of order) {
        if (paired.has(a)) continue;
        const newsScores=new Map(),newsScore=id=>{
          if(!newsScores.has(id))newsScores.set(id,listenerNewsScore(s,a,id));
          return newsScores.get(id);
        };
        const locals = byVillage.get(ecs.village[a]?.villageId) || [];
        const known = ecs.relationships[a].known,
          seekingPartner = singles.has(a);
        const localOrFriend = new Set(locals);
        for (const id of peopleInRings(s, a, ["social", "personal", "intimate"])) if (live.has(id)) localOrFriend.add(id);
        const avoided = avoiding.get(a);
        let b,
          best = -Infinity,
          bestInterest = 0;
        for (const pool of seekingPartner ? [localOrFriend, singles] : [localOrFriend]) for (const id of pool) {
          const local = localOrFriend.has(id);
          if (pool == singles && local) continue;
          if (id == a || paired.has(id)) continue;
          if (avoided?.has(id)) continue;
          const seeking = seekingPartner && singles.has(id) && !related(s, a, id);
          if (!local && !seeking) continue;
          const interest = seeking ? partnerInterest(s,a,id) : 0,
            score = interest ? courtshipPreference(s, a, id, ecs.village[a]?.villageId === ecs.village[id]?.villageId ? 1 : .35) : known[id]?.goodwill || 0;
          if (interest > bestInterest || interest == bestInterest && (score > best || score == best && (!b || newsScore(id)>newsScore(b) || newsScore(id)==newsScore(b) && id.localeCompare(b)<0))) {
            b = id;
            best = score;
            bestInterest = interest;
          }
        }
        if (!b) continue;
        paired.add(a);
        paired.add(b);
        if (s.settlement && ecs.village[a]?.villageId != ecs.village[b]?.villageId) recordJourney(s,{key:"visit:"+season+":"+a+":"+b,people:[a],from:routeTile(s,ecs.spatial[ecs.household[a]?.houseId]),to:routeTile(s,ecs.spatial[ecs.household[b]?.houseId]),reason:"visiting household",mainRoute:true,day:s.days,returnDay:s.days+.2,count:1});
        chatTogether(s, a, b, true, 6);
      }
    }
  });
  if(summary)historySecretActivities(s,participants,season);
  updateCourtships(s,season,summary,roster);
  const birthVillages=Object.keys(s.villages || {});
  const occupants=householdCounts(s,participants);
  if(participants.some(id=>ecs.village[id]?.villageId==null && (ecs.building?.[ecs.household[id]?.houseId] || ecs.shelter?.[ecs.household[id]?.houseId]?.stage=="house")))birthVillages.push(null);
  for (const villageId of birthVillages) {
    const locals = participants.filter(id => (ecs.village[id]?.villageId ?? null) == villageId);
    const target = (s.villages?.[villageId]?.houses?.length || new Set(locals.map(id=>ecs.household[id]?.houseId)).size || 3) * 3;
    const fertility = Math.min(2, Math.max(0.2, target / Math.max(1, locals.length)));
    for (const a of locals) {
      const b = ecs.kinship[a].spouse,
        p = ecs.personable[a];
      if (!b || !isResident(s, b) || a > b || !alive(s, b) || ecs.kinship[b]?.spouse != a || !canHaveChild(s,a,b)) continue;
      const house = ecs.household[a]?.houseId;
      if ((occupants[house] || 0) >= HOUSEHOLD_CAPACITY) continue;
      if (season - Math.max(p.lastBirth ?? -10000, ecs.personable[b].lastBirth ?? -10000) < SOCIAL_RATES.birthCooldownSeasons) continue;
      if (roll(s, "birth:" + a + ":" + season) < Math.min(SOCIAL_RATES.maxBirthChance, SOCIAL_RATES.birthPerSeason * fertility)) {
        birth(s, a, b, season);
        occupants[house]=(occupants[house]||0)+1;
      }
    }
  }
  repairHouseholds(s);
  settleSeason(s,season,summary);
  s.social.lastSeason = season;
}
export function historyStep(worldHexState, season) {
  if (worldHexState.social?.lastSeason >= season) return worldHexState;
  // Events, names and geography are immutable. Copy only the records this step
  // can change; a generated history must not recopy the map or old events.
  const copy = {
    ...worldHexState,
    peopleDirectory: worldHexState.peopleDirectory,
    eventIds: worldHexState.eventIds && { ...worldHexState.eventIds },
    ecs: Object.fromEntries(Object.entries(worldHexState.ecs).map(([key, value]) => [key, typeof value == "object" ? {
      ...value
    } : value])),
    residents: worldHexState.residents && {
      ...worldHexState.residents
    },
    events: (worldHexState.events || []).map(e=>e.awareness ? {...e,awareness:structuredClone(e.awareness)} : e),
    knowledge: {},
    villages: {},
    space: {
      ...Object.fromEntries(Object.entries(worldHexState.space || {}).map(([key, ids]) => [key, [...ids]]))
    },
    redraws: [...(worldHexState.redraws || [])],
    chat: worldHexState.chat && JSON.parse(JSON.stringify(worldHexState.chat)),
    social: JSON.parse(JSON.stringify(worldHexState.social || {}))
  };
  const records = (key, clone = value => ({
    ...value
  })) => {
    copy.ecs[key] = Object.fromEntries(Object.entries(worldHexState.ecs[key] || {}).map(([id, value]) => [id, clone(value, id)]));
  };
  records("personable");
  records("controllable");
  records("household", h => JSON.parse(JSON.stringify(h)));
  records("profession");
  records("container", c => ({ ...c, amounts: { ...c.amounts } }));
  records("holder", h => ({ ...h, held: [...h.held] }));
  records("haulable");
  records("spatial");
  records("kinship", k => ({
    ...k,
    parents: [...k.parents],
    children: [...k.children]
  }));
  records("relationships", (r, id) => worldHexState.ecs.personable[id]?.dead ? r : {
    ...r,
    known: copyRelationshipList(r.known),
    ...(r.actionMoments?{actionMoments:{...r.actionMoments}}:{}),
    ...(r.unmetGoodwill?{unmetGoodwill:{...r.unmetGoodwill}}:{})
  });
  records("possession", p => ({
    ...p,
    owners: [...p.owners]
  }));
  records("planner", p => ({
    ...p,
    world: {
      ...p.world
    }
  }));
  records("manager", m => ({
    ...m,
    jobs: m.jobs.map(j => ({
      ...j
    }))
  }));
  records("sleepable", b => ({
    ...b,
    occupiers: [...b.occupiers]
  }));
  records("farmable", f => ({
    ...f,
    slots: f.slots.map(slot => ({
      ...slot
    }))
  }));
  records("building", b => ({
    ...b,
    rooms: [...(b.rooms || [])]
  }));
  for (const [id, k] of Object.entries(worldHexState.knowledge || {})) copy.knowledge[id] = {
    ...k,
    ...(k.spokenSources?{spokenSources:{...k.spokenSources}}:{}),
    ...(k.reactions?{reactions:{...k.reactions}}:{}),
    ...(k.discoveries?{discoveries:{...k.discoveries}}:{}),
    ...(k.newsScopes?{newsScopes:{...k.newsScopes}}:{}),
    ...(k.people?{people:{...k.people}}:{}),
    events: [...(k.events || [])]
  };
  for (const [id, v] of Object.entries(worldHexState.villages || {})) copy.villages[id] = {
    ...v,
    houses: [...(v.houses || [])]
  };
  if (worldHexState.settlement) {
    copy.settlement=JSON.parse(JSON.stringify(worldHexState.settlement));
    for (const key of ["growth","workplace","meetingPlace","container","treeClearance","mappable","spatial","walkable","tree","good"]) records(key,value=>JSON.parse(JSON.stringify(value)));
    for (const key in copy.space) copy.space[key]=[...copy.space[key]];
    copy.map=worldHexState.map && {...worldHexState.map};
    for (const [id,v] of Object.entries(worldHexState.villages || {})) copy.villages[id]=JSON.parse(JSON.stringify(v));
  }
  return withDetachedPeople(copy,()=>{
    copy.days = season * DAYS_IN_SEASON;
    drift(copy, 24 * DAYS_IN_SEASON);
    advanceOffscreenNeeds(copy, season * DAYS_IN_SEASON);
    advanceSeason(copy, season, true);
    return copy;
  });
}
